/** @prettier */

/**
 * グループレイアウト
 */

import {Grid, Card, CardContent, Stack, Box} from '@mui/material';
import {useLoaderData, useParams} from 'react-router-dom';
import {useEffect, useState} from 'react';
import type {LoaderFunctionArgs} from 'react-router-dom';

//MTH component import
import GroupLayoutSetting from '../../component/groupLayout/GroupLayoutSetting';
import GroupUnitEditDialog from './GroupUnitEditDialog';
import CardContents from '../../component/CardContents';
//API import
import {
  getCompositionsGroupsByGroupID,
  getCompositionsGroups,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';
//type import
import {layoutUnitListType} from '../../Type/TypeDef';

import {useOutlet} from '../group-registration/HCCVQGroupMain';
import {sortByUnicode} from '../../functions/sort';

import {useTranslation} from 'react-i18next';

export async function loader({
  params,
}: LoaderFunctionArgs): Promise<
  apiIF.res.getCompositionsGroupsByGroupID | undefined
> {
  //パスパラメータを取得できない場合
  if (params.siteID === undefined || params.groupID === undefined) {
    return undefined;
  }

  try {
    //表示しているグループの情報を取得
    const response = await getCompositionsGroupsByGroupID(
      params.siteID,
      params.groupID
    );
    //表示内容をUnicode順に並び替え
    response['unit-informations'].sort(sortByUnicode('unit-name'));

    return response;
  } catch {
    return undefined;
  }
}

const GroupLayoutRegistration = (props: {unitSystem: 'RT' | 'Modbus'}) => {
  const loaderData = useLoaderData() as
    | apiIF.res.getCompositionsGroupsByGroupID
    | undefined;
  //パスパラメータ取得
  const {siteID} = useParams();
  //親コンポーネントから値を取得
  const {setAllGroupInfo} = useOutlet();

  const [displayGroupInfo, setDisplayGroupInfo] =
    useState<apiIF.res.getCompositionsGroupsByGroupID | null>(null); //表示しているグループの情報

  const [unitList, setUnitList] = useState<layoutUnitListType>(); //ユニット情報

  const trans = useTranslation().t; //翻訳関数

  useEffect(() => {
    if (loaderData !== undefined) {
      setDisplayGroupInfo(loaderData);
    }
  }, [loaderData]);

  //リストから室内機の登録が変更されたとき、ツリービューの表示を更新する
  useEffect(() => {
    //パスパラメータを取得できない場合
    if (siteID === undefined) {
      return;
    }

    //サイトに登録されているすべてのグループの情報を取得
    getCompositionsGroups(siteID, {'max-result': 400}).then(response => {
      //表示内容をUnicode順に並び替え
      response['group-informations'].sort(sortByUnicode('group-name'));
      for (const info of response['group-informations']) {
        if (info['unit-informations'] === undefined) continue;
        info['unit-informations'].sort(sortByUnicode('unit-name'));
      }
      setAllGroupInfo(response);
    });

    if (displayGroupInfo !== null) {
      setUnitList(
        displayGroupInfo['unit-informations'].map(info => {
          return {
            unitID: info['unit-id'],
            unitType: 'Hyozan',
          };
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayGroupInfo, siteID]);

  //グループの情報を取得できない場合
  if (displayGroupInfo === null) {
    return <></>;
  }

  return (
    <>
      {/* グループ名称の表示 */}
      <Box className="groupSetting__title">
        {displayGroupInfo['group-name']}
      </Box>
      <Stack spacing={4}>
        {/* グループレイアウト表示 */}
        <GroupLayoutSetting
          displayGroupInfo={displayGroupInfo}
          setDisplayGroupInfo={setDisplayGroupInfo}
          unitList={unitList}
        />
        {/* 室内機リスト表示 */}
        <Card className="groupSetting__contents--layout">
          <CardContent>
            <Stack spacing={2}>
              {/* 登録室内機編集ボタン */}
              <Grid
                container
                justifyContent="space-between"
                className="groupSetting__contents__title">
                <Grid item xs={2}>
                  {trans('GroupLayoutRegistration.title')}
                </Grid>
                <Grid item xs={'auto'}>
                  <GroupUnitEditDialog
                    unitSystem={props.unitSystem}
                    displayGroupInfo={displayGroupInfo}
                    setDisplayGroupInfo={setDisplayGroupInfo}
                  />
                </Grid>
              </Grid>
              {/* 室内機登録台数と名前 */}
              <Stack spacing={2} className="groupSetting__contents__contents">
                {/* 室内機登録台数 */}
                <CardContents
                  class="groupSetting__contents__contents__underLine"
                  valueDescription={trans(
                    'GroupLayoutRegistration.item.title.numberOfIndoorUnits'
                  )}
                  value={String(displayGroupInfo['unit-informations'].length)}
                />
                {/* 登録室内機名称 */}
                <CardContents
                  class="groupSetting__contents__contents__underLine"
                  valueDescription={trans(
                    'GroupLayoutRegistration.item.title.indoorUnit'
                  )}
                  value={
                    <Stack spacing={0}>
                      {displayGroupInfo['unit-informations'].map(groupUnit => (
                        <Box key={groupUnit['unit-id']}>
                          {groupUnit['unit-name']}
                        </Box>
                      ))}
                    </Stack>
                  }
                />
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </>
  );
};
export default GroupLayoutRegistration;
